import { STATE_DEPARTMENTS_VALIDATION_LIMITS } from "../constants/stateDepartmentsConstants";

// Validate amount
export const validateAmount = (amount, fieldName = "Amount") => {
  if (amount === "" || amount === null || amount === undefined) {
    return null; // Allow empty amounts (will be treated as 0)
  }

  const numAmount = parseFloat(amount);

  if (isNaN(numAmount)) {
    return `${fieldName} must be a valid number`;
  }

  if (numAmount < STATE_DEPARTMENTS_VALIDATION_LIMITS.AMOUNT_MIN) {
    return `${fieldName} cannot be negative`;
  }

  if (numAmount > STATE_DEPARTMENTS_VALIDATION_LIMITS.AMOUNT_MAX) {
    return `${fieldName} cannot exceed ₹99 crores`;
  }

  return null;
};

// Validate department name
export const validateDepartmentName = (name) => {
  if (!name || !name.trim()) {
    return "Department name is required";
  }

  const trimmedName = name.trim();

  if (
    trimmedName.length <
    STATE_DEPARTMENTS_VALIDATION_LIMITS.DEPT_NAME_MIN_LENGTH
  ) {
    return `Department name must be at least ${STATE_DEPARTMENTS_VALIDATION_LIMITS.DEPT_NAME_MIN_LENGTH} characters`;
  }

  if (
    trimmedName.length >
    STATE_DEPARTMENTS_VALIDATION_LIMITS.DEPT_NAME_MAX_LENGTH
  ) {
    return `Department name cannot exceed ${STATE_DEPARTMENTS_VALIDATION_LIMITS.DEPT_NAME_MAX_LENGTH} characters`;
  }

  return null;
};

// Validate departments data
export const validateDepartmentsData = (departmentsData) => {
  const errors = {};

  if (!departmentsData.financialYear) {
    errors.financialYear = "Financial year is required";
  }

  if (
    !departmentsData.departments ||
    departmentsData.departments.length === 0
  ) {
    errors.departments = "At least one department is required";
  } else {
    departmentsData.departments.forEach((dept, index) => {
      const nameError = validateDepartmentName(dept.name);
      if (nameError) {
        errors[`departments.${index}.name`] = nameError;
      }

      const amountError = validateAmount(dept.amount, "Amount");
      if (amountError) {
        errors[`departments.${index}.amount`] = amountError;
      }

      const duplicateIndex = departmentsData.departments.findIndex(
        (d, i) => i !== index && (d.value === dept.value || d.id === dept.id),
      );
      if (duplicateIndex !== -1) {
        errors[`departments.${index}.duplicate`] =
          "This department has already been added";
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Sanitize departments data
export const sanitizeDepartmentsData = (departmentsData) => {
  const sanitized = { ...departmentsData };

  if (sanitized.departments && sanitized.departments.length > 0) {
    sanitized.departments = sanitized.departments.map((dept) => ({
      id: dept.id || dept.value,
      value: dept.value || dept.id,
      name: dept.name?.trim() || "",
      amount:
        dept.amount === "" || dept.amount === null || dept.amount === undefined
          ? 0
          : parseFloat(dept.amount) || 0,
    }));

    sanitized.departments = sanitized.departments.filter(
      (dept) => dept.id && dept.name && dept.name.trim() !== "",
    );
  } else {
    sanitized.departments = [];
  }

  sanitized.totalAmount = sanitized.departments.reduce((total, dept) => {
    return total + (parseFloat(dept.amount) || 0);
  }, 0);

  return sanitized;
};

// Validate individual department for real-time validation
export const validateDepartment = (department, existingDepartments = []) => {
  const errors = {};

  if (!department.id && !department.value) {
    errors.id = "Department ID is required";
  }

  const nameError = validateDepartmentName(department.name);
  if (nameError) {
    errors.name = nameError;
  }

  const amountError = validateAmount(department.amount, "Amount");
  if (amountError) {
    errors.amount = amountError;
  }

  const isDuplicate = existingDepartments.some(
    (existing) =>
      (existing.id === department.id || existing.value === department.value) &&
      existing !== department,
  );

  if (isDuplicate) {
    errors.duplicate = "This department has already been added";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validate amount change for real-time feedback
export const validateAmountChange = (newAmount, departmentName = "") => {
  const amountError = validateAmount(newAmount, `Amount for ${departmentName}`);

  if (amountError) {
    return {
      isValid: false,
      error: amountError,
    };
  }

  const numAmount = parseFloat(newAmount) || 0;

  if (numAmount > 10000) {
    return {
      isValid: true,
      warning: "This is a very large allocation. Please verify the amount.",
    };
  }

  return {
    isValid: true,
  };
};

// Validate financial year format — starts from 1990
export const validateFinancialYear = (financialYear) => {
  if (!financialYear) {
    return "Financial year is required";
  }

  const fyPattern = /^\d{4}-\d{2}$/;
  if (!fyPattern.test(financialYear)) {
    return "Financial year must be in YYYY-YY format (e.g., 2024-25)";
  }

  const [startYear, endYearShort] = financialYear.split("-");
  const startYearNum = parseInt(startYear);
  const endYearNum = parseInt("20" + endYearShort);

  if (endYearNum !== startYearNum + 1) {
    return "Invalid financial year sequence";
  }

  const currentYear = new Date().getFullYear();
  if (startYearNum < 1990 || startYearNum > currentYear + 5) {
    return "Financial year is outside the valid range (1990 onwards)";
  }

  return null;
};

export default {
  validateAmount,
  validateDepartmentName,
  validateDepartmentsData,
  sanitizeDepartmentsData,
  validateDepartment,
  validateAmountChange,
  validateFinancialYear,
};